import { Flex, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ShareJobPost from "../../Components/ShareJobPost/ShareJobPost";
import useOrganization from "../../Hooks/useOrganization";
import {
  checkSubscriptionStatus,
  showSubscriptionNotification,
} from "../../Utils/subscriptionUtils";
import JobPostTable from "./JobPostTable";
import JobPostCardView from "./JobPostCardView";
import JobPostsFilterMenu from "./JobPostsFilterMenu";
import { JobPostsFilters, JobPostsPagination } from "../../Interface";
import { JobDescriptionEntity } from "../../Entities/JobDescription";
import jobDescriptionService from "../../Service/job.description.service";
import { useLoader } from "../../Context/LoaderContext";
import JobPostHeader from "./JobPostHeader";
import SubscriptionWarning from "../../Components/SubscriptionWarning";
import "./index.scss";
import { useSearchParams } from "react-router-dom";

const JobPosts = () => {
  const navigate = useNavigate();
  const { setLoadingMessage, setLoading } = useLoader();
  const { org } = useOrganization();
  const [open, setOpen] = useState<boolean>(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabKey = searchParams.get("tab") || "2";

  const defaultFilters = (): JobPostsFilters => ({
    search: null,
    status: null,
    sort: null,
    order: null,
  });

  const defaultPagination = (): JobPostsPagination => ({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
  });

  const [tabState, setTabState] = useState<Record<string, {
    jobPosts: JobDescriptionEntity[];
    filters: JobPostsFilters;
    pagination: JobPostsPagination;
  }>>({
    "1": { jobPosts: [], filters: defaultFilters(), pagination: defaultPagination() },
    "2": { jobPosts: [], filters: defaultFilters(), pagination: defaultPagination() },
  });

  const [viewMode, setViewMode] = useState<"table" | "card">("table");

  const currentState = tabState[activeTabKey];
  const jobPosts = currentState.jobPosts;
  const filters = currentState.filters;
  const pagination = currentState.pagination;

  const setTabFilters = (updater: (prev: JobPostsFilters) => JobPostsFilters) => {
    setTabState((prev) => ({
      ...prev,
      [activeTabKey]: {
        ...prev[activeTabKey],
        filters: updater(prev[activeTabKey].filters),
        pagination: { ...prev[activeTabKey].pagination, page: 1 },
      },
    }));
  };

  const setPagination = (updater: JobPostsPagination | ((prev: JobPostsPagination) => JobPostsPagination)) => {
    setTabState((prev) => ({
      ...prev,
      [activeTabKey]: {
        ...prev[activeTabKey],
        pagination: typeof updater === "function" ? updater(prev[activeTabKey].pagination) : updater,
      },
    }));
  };

  const updateFilters = (key: keyof JobPostsFilters, value: string | null) => {
    setTabFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSortFilter = (
    sort: keyof JobDescriptionEntity | null,
    order: "asc" | "desc" | null,
  ) => {
    setTabFilters((prev) => ({
      ...prev,
      sort: sort ?? null,
      order: order ?? null,
    }));
  };

  const clearAllFilters = () => {
    setTabFilters(() => defaultFilters());
  };

  const fetchJobPosts = async () => {
    setLoading(true);
    setLoadingMessage("Fetching job posts...");
    try {
      const filterByCreator = activeTabKey === "2"; // Tab 2 = "My Job Posts", Tab 1 = "All Job Posts"
      const { filters: currentFilters, pagination: currentPagination } = tabState[activeTabKey];
      const response =
        await jobDescriptionService.getAllJobDescriptionwithFilters(
          currentPagination.page,
          currentPagination.limit,
          currentFilters,
          filterByCreator,
        );

      if (response && response.jobPosts) {
        setTabState((prev) => ({
          ...prev,
          [activeTabKey]: {
            ...prev[activeTabKey],
            jobPosts: response.jobPosts,
            pagination: {
              ...prev[activeTabKey].pagination,
              totalRecords: response.totalRecords || 0,
              totalPages: response.totalPages || 0,
            },
          },
        }));
      } else {
        console.error(
          "Invalid response from getAllJobDescriptionwithFilters:",
          response,
        );
        setTabState((prev) => ({
          ...prev,
          [activeTabKey]: {
            ...prev[activeTabKey],
            jobPosts: [],
            pagination: { ...prev[activeTabKey].pagination, totalRecords: 0, totalPages: 0 },
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching job posts:", error);
      setTabState((prev) => ({
        ...prev,
        [activeTabKey]: {
          ...prev[activeTabKey],
          jobPosts: [],
          pagination: { ...prev[activeTabKey].pagination, totalRecords: 0, totalPages: 0 },
        },
      }));
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  // Set default tab in URL if not present (run once on mount)
  useEffect(() => {
    if (!searchParams.get("tab")) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("tab", "2");
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once and avoid infinite loop

  useEffect(() => {
    fetchJobPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabState[activeTabKey].pagination.page, tabState[activeTabKey].pagination.limit, tabState[activeTabKey].filters, activeTabKey]);

  const handleAddPost = async () => {
    const subscriptionCheck = await checkSubscriptionStatus();
    if (!subscriptionCheck.canAddPost) {
      showSubscriptionNotification(
        subscriptionCheck.message || "Upgrade Required",
      );
      setOpen(true);
      return;
    }
    navigate("/add-job-post");
  };

  const items = [
    {
      key: "1",
      label: "All Job Posts",
    },
    {
      key: "2",
      label: "My Job Posts",
    },
  ];

  const handleTabChange = (key: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", key);
    setSearchParams(newSearchParams);
  };

  const handleShareModalClose = () => {
    setShareModalVisible(false);
  };


  const getTabChildren = (tab: string) => {
    if (tab === "1") {
      return renderAllJobPosts();
    } else if (tab === "2") {
      return renderMyJobPosts();
    }
  };

  const renderAllJobPosts = () => {
    return (
      <>
        <JobPostsFilterMenu
          updateFilters={updateFilters}
          filters={filters}
          clearAllFilters={clearAllFilters}
          handleSortFilter={handleSortFilter}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {viewMode === "table" ? (
          <JobPostTable
            jobPosts={jobPosts}
            fetchJobPosts={fetchJobPosts}
            pagination={pagination}
            setPagination={setPagination}
          />
        ) : (
          <JobPostCardView
            jobPosts={jobPosts}
            fetchJobPosts={fetchJobPosts}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
      </>
    );
  };

  const renderMyJobPosts = () => {
    return (
      <>
        <JobPostsFilterMenu
          updateFilters={updateFilters}
          filters={filters}
          clearAllFilters={clearAllFilters}
          handleSortFilter={handleSortFilter}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {viewMode === "table" ? (
          <JobPostTable
            jobPosts={jobPosts}
            fetchJobPosts={fetchJobPosts}
            pagination={pagination}
            setPagination={setPagination}
          />
        ) : (
          <JobPostCardView
            jobPosts={jobPosts}
            fetchJobPosts={fetchJobPosts}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
      </>
    );
  };


  return (
    <section style={{ gap: 0 }} className="dashboard-page job-posts-page hide-scroll-bar">
      <JobPostHeader handleAddPost={handleAddPost} />
      <Tabs
        activeKey={activeTabKey}
        items={items}
        onChange={handleTabChange}
        size="small"
        style={{ margin: "0px", width: "100%", overflowY: "auto", height: "max-content" }}
      />
      <div style={{ overflowY: "hidden" }} className="team-members-page-tab-children-wrapper">
        {getTabChildren(activeTabKey)}
      </div>
      <SubscriptionWarning
        open={open}
        handleClose={() => setOpen(false)}
        title="Limit reached"
        type="job-post-limit"
      />
      <ShareJobPost
        visible={shareModalVisible}
        onClose={handleShareModalClose}
        orgDomain={org?.orgDomain || ""}
      />
    </section>
  );
};

export default JobPosts;
